import React from 'react';

import { TrackableItem } from 'types/trackables';
import { FilesSearchResult } from 'types/files_search_result';
import { CursorPaginated } from 'types/pagination';
import { Stream } from 'types/stream';
import { Camera } from 'types/camera';
import { LumeoFile } from 'types/file';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useFilesSearch } from 'files/hooks/useFilesSearch';
import { useTrackableNames } from './useTrackableNames';
import { useStreams } from 'streams/hooks/useStreams';
import { useCameras } from 'cameras/hooks/useCameras';
import { useFiles } from 'files/hooks/useFiles';
import { isNotNullOrUndefined } from 'services/nullable';

const TRACKABLE_ITEMS_PAGE_LIMIT = 150;

export type UseWatchlistStatusParams = {
  /** IDs of watchlists to fetch all items from */
  watchlistIds: string[];
  /** Specific item IDs to monitor (takes precedence over watchlistIds for item selection) */
  watchlistItemIds?: string[];
  /** Start of time range (ISO 8601) */
  timeRangeStart: string;
  /** End of time range (ISO 8601) */
  timeRangeStop: string;
  /** Gateway IDs to filter by */
  gatewayIds?: string[];
  /** Whether to enable queries */
  enabled?: boolean;
  /** Refetch interval in ms */
  refetchInterval?: number;
};

export type UseWatchlistStatusResult = {
  /** Resolved trackable items to display */
  trackableItems: TrackableItem[];
  /** Map of trackable ID to its latest match */
  matchesByTrackableId: Map<string, FilesSearchResult>;
  /** Map of stream ID to stream */
  streamsMap: Map<string, Stream>;
  /** Map of camera ID to camera */
  camerasMap: Map<string, Camera>;
  /** Map of file ID to file */
  filesMap: Map<string, LumeoFile>;
  /** Whether any data is still loading */
  isLoading: boolean;
  /** Whether items should be fetched (at least one list or item specified) */
  shouldFetchItems: boolean;
};

export function useWatchlistStatus({
  watchlistIds,
  watchlistItemIds = [],
  timeRangeStart,
  timeRangeStop,
  gatewayIds,
  enabled = true,
  refetchInterval,
}: UseWatchlistStatusParams): UseWatchlistStatusResult {
  const { applicationID, client } = useAPI();

  const hasExplicitItemSelection = watchlistItemIds.length > 0;
  const shouldFetchItems = watchlistIds.length > 0 || hasExplicitItemSelection;
  const shouldFetchAllItems = watchlistIds.length > 0 && !hasExplicitItemSelection;

  // Fetch all trackable items from selected lists (when no explicit items selected)
  const {
    data: trackableItemsData,
    isInitialLoading: isLoadingItems,
    isFetching: isFetchingItems,
  } = useAuthenticatedQuery<TrackableItem[]>(
    ['trackable-items-all-pages', applicationID, watchlistIds],
    async () => {
      if (!applicationID || watchlistIds.length === 0) {
        return [];
      }

      const items: TrackableItem[] = [];
      let nextCursor: string | null = null;

      do {
        const params: Record<string, unknown> = {
          trackable_list_ids: watchlistIds,
          pagination: 'cursor',
          limit: TRACKABLE_ITEMS_PAGE_LIMIT,
        };

        if (nextCursor) {
          params.next_cursor = nextCursor;
        }

        const { data } = await client.get<CursorPaginated<TrackableItem>>(
          `/apps/${applicationID}/trackable_items`,
          { params }
        );

        items.push(...data.data);
        nextCursor = data.next_cursor;
      } while (nextCursor !== null);

      return items;
    },
    {
      enabled: enabled && shouldFetchAllItems && Boolean(applicationID),
      keepPreviousData: true,
    }
  );

  // Fetch metadata for explicitly selected items
  const trackableNamesQuery = useTrackableNames(watchlistItemIds);

  // Resolve the final list of trackable items
  const resolvedTrackableItems = React.useMemo(() => {
    if (hasExplicitItemSelection) {
      const itemsLookup = trackableNamesQuery.data?.items ?? {};
      return watchlistItemIds
        .map((id) => itemsLookup[id])
        .filter((item): item is TrackableItem => Boolean(item));
    }

    return trackableItemsData ?? [];
  }, [
    hasExplicitItemSelection,
    watchlistItemIds,
    trackableItemsData,
    trackableNamesQuery.data,
  ]);

  // Get the IDs to search for
  const trackableIds = React.useMemo(
    () =>
      hasExplicitItemSelection
        ? watchlistItemIds
        : resolvedTrackableItems.map((item) => item.id),
    [hasExplicitItemSelection, watchlistItemIds, resolvedTrackableItems]
  );

  // Determine metadata loading state
  const isLoadingTrackableMetadata = hasExplicitItemSelection
    ? trackableNamesQuery.isInitialLoading
    : isLoadingItems;
  const isFetchingTrackableMetadata = hasExplicitItemSelection
    ? trackableNamesQuery.isFetching
    : isFetchingItems;
  const metadataLoading =
    isLoadingTrackableMetadata ||
    (isFetchingTrackableMetadata && resolvedTrackableItems.length === 0);

  // Search for latest matches
  const {
    data: searchData,
    isInitialLoading: isLoadingSearch,
    isFetching: isFetchingSearch,
  } = useFilesSearch({
    params: {
      query: trackableIds.join(' '),
      search_entities: ['trackables'],
      created_ts_since: timeRangeStart,
      created_ts_until: timeRangeStop,
      gateway_ids: gatewayIds,
      sort_by: 'created_at',
      page: 1,
      limit: trackableIds.length,
      latest_trackable_hit: true,
    },
    keepPreviousData: true,
    refetchInterval,
    enabled: enabled && trackableIds.length > 0,
  });

  // Build map of trackable ID to match
  const matchesByTrackableId = React.useMemo(() => {
    const map = new Map<string, FilesSearchResult>();
    if (!searchData?.data?.length) {
      return map;
    }

    for (const result of searchData.data) {
      if (result.latest_trackable_hit) {
        map.set(result.latest_trackable_hit, result);
      }
    }

    return map;
  }, [searchData]);

  const matches = React.useMemo(
    () => Array.from(matchesByTrackableId.values()),
    [matchesByTrackableId]
  );

  // Fetch files for matches
  const fileIds = React.useMemo(
    () =>
      Array.from(new Set(matches.map((match) => match.file_id))).filter(Boolean),
    [matches]
  );

  const { data: filesData } = useFiles({
    params: {
      file_ids: fileIds,
      limit: Math.max(fileIds.length, 1),
      include_snapshots: false,
      with_thumbnail: false,
    },
    enabled: enabled && fileIds.length > 0,
    keepPreviousData: true,
  });

  const filesMap = React.useMemo(
    () =>
      new Map<string, LumeoFile>(
        filesData?.data.map((file) => [file.id, file]) ?? []
      ),
    [filesData]
  );

  // Fetch streams for matches
  const streamIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          matches.map((match) => match.stream_id).filter(isNotNullOrUndefined)
        )
      ),
    [matches]
  );

  const { data: streamsData } = useStreams({
    params: {
      stream_ids: streamIds,
      limit: streamIds.length,
    },
    enabled: enabled && streamIds.length > 0,
  });

  const streamsMap = React.useMemo(() => {
    if (!streamsData?.data) {
      return new Map<string, Stream>();
    }

    return new Map<string, Stream>(
      streamsData.data.map((stream) => [stream.id, stream])
    );
  }, [streamsData]);

  // Fetch cameras (from matches and streams)
  const cameraIds = React.useMemo(() => {
    const fromMatches = matches
      .map((match) => match.camera_id)
      .filter(isNotNullOrUndefined);

    const fromStreams = streamsData?.data
      ? streamsData.data
          .map((stream) => stream.camera_id)
          .filter(isNotNullOrUndefined)
      : [];

    return Array.from(new Set([...fromMatches, ...fromStreams]));
  }, [matches, streamsData]);

  const { data: camerasData } = useCameras({
    params: {
      camera_ids: cameraIds,
      limit: cameraIds.length,
    },
    enabled: enabled && cameraIds.length > 0,
  });

  const camerasMap = React.useMemo(() => {
    if (!camerasData?.data) {
      return new Map<string, Camera>();
    }

    return new Map<string, Camera>(
      camerasData.data.map((camera) => [camera.id, camera])
    );
  }, [camerasData]);

  // Compute overall loading state
  const isLoading =
    shouldFetchItems &&
    (metadataLoading || isLoadingSearch || (isFetchingSearch && !searchData));

  return {
    trackableItems: resolvedTrackableItems,
    matchesByTrackableId,
    streamsMap,
    camerasMap,
    filesMap,
    isLoading,
    shouldFetchItems,
  };
}
